import {
  Alert,
  Box,
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  TextField,
  Typography
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { useEffect, useState } from 'react';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useTranslation } from 'react-i18next';
import useLogin from '@src/ui/hooks/authentication/useLogin';
import { Admin } from '@src/domain/interfaces/admin';
import axiosInstance from '@src/infrastructure/services/config';
import { authAtom } from '@src/domain/atoms/authAtom';
import { useSetRecoilState } from 'recoil';
import useRedirect from '@src/ui/hooks/useRedirect';
import Logo from '../../../assets/images/Budoar-Atelier-Logo.png';

const LoginPage = (): JSX.Element => {
  const navigate = useNavigate();
  const redirect = useRedirect();

  useEffect(() => {
    if (redirect) {
      navigate('/')
    }  
  })
  const setAuthed= useSetRecoilState(authAtom)

  const [isError, setIsError] = useState<any>()

  const { handleSubmit, control } = useForm<any>({
    defaultValues: '',
    mode: 'onBlur'
  });
  const { t, i18n } = useTranslation();

  const [showPassword, setShowPassword] = useState<boolean>(false);

  const handleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const loginMutation = useLogin();

  const handleLogin = (data: Admin) => {
    loginMutation.mutate(data, {
      onError: (error: any) => {
        setIsError(error.response.data.Error);
        if (error.response.status === 401) {
          setAuthed(false);
          navigate('/');
          axiosInstance.post('/logout');
          localStorage.removeItem('token');
          localStorage.removeItem('adminID');
        }
      },
      onSuccess: res => {
        const token = res.data.Bearer;
        localStorage.setItem('token', token);
        const adminID = res.data.Admin.ID;
        localStorage.setItem('adminID', adminID);
        setAuthed(true);
        navigate('/');
      }
    });
  };

  const windowHeight = window.innerHeight;

  return (
    <Box
      component={'form'}
      onSubmit={handleSubmit(handleLogin)}
      sx={{
        alignItems: 'center',
        justifyContent: 'center',
        display: 'flex',
        height: windowHeight
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          justifyContent: 'center',
          display: 'flex',
          flexDirection: 'column',
          padding: '10px'
        }}
      >
        <Typography variant="h2" alignSelf={'center'}>
          <img src={Logo} alt="Budoar Logo" />
        </Typography>
        <Controller
          name="Email"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <FormControl margin="normal" sx={{width: 250}}>
              <TextField
                label={t('email')}
                autoFocus={true}
                {...field}
                sx={{
                  '& .MuiInputLabel-outlined.Mui-focused': {
                    color: 'black'
                  },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'black',
                    opacity: 0.8
                  }
                }}
              />
            </FormControl>
          )}
        />
        <Controller
          name="Password"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <FormControl
              margin="normal"
              sx={{
                '& .MuiInputLabel-outlined.Mui-focused': {
                  color: 'black'
                },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'black',
                  opacity: 0.8
                }
              }}
            >
              <InputLabel htmlFor="Password">{t('password')}</InputLabel>
              <OutlinedInput
                sx={{ width: 250 }}
                id="Password"
                {...field}
                type={showPassword ? 'text' : 'password'}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton onClick={handleShowPassword} edge="end">
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                }
                label={t('password')}
              />
            </FormControl>
          )}
        />
          {isError ? <Alert severity="error">{isError}</Alert> : ''}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            marginTop: '1rem',
            width: 'max-content'
          }}
        >
          <Button
            sx={{
              border: 'solid',
              borderRadius: '4px',
              backgroundColor: '#e9e3dc',
              borderColor: '#101010',
              color: '#101010',
              width: 250,
              borderWidth: '1px'
            }}
            fullWidth
            type="submit"
            disableRipple
          >
            {t('login')}
          </Button>
          <Button
            sx={{
              border: 'solid',
              borderRadius: '4px',
              backgroundColor: '#e9e3dc',
              borderColor: '#101010',
              color: '#101010',
              width: 250,
              borderWidth: '1px'
            }}
            fullWidth
            disableRipple
            onClick={() => navigate('/')}
          >
            {t('backHome')}
          </Button>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: '2rem',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer'
            }}
          >
            <img
              src="data:image/png;base64,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"
              alt="English Flag"
              height={30}
              width={40}
              onClick={() => i18n.changeLanguage('en')}
            />
            <img
              src="https://upload.wikimedia.org/wikipedia/commons/thumb/f/ff/Flag_of_Serbia.svg/800px-Flag_of_Serbia.svg.png?20230930181708"
              alt="Srpska Zastava"
              height={30}
              width={40}
              onClick={() => i18n.changeLanguage('sr')}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LoginPage;

